//
// Copyright 2023 DXOS.org
//

// @import-as-namespace

import * as Schema from 'effect/Schema';

import * as AppAnnotation from '@dxos/app-toolkit/AppAnnotation';
import { addressFromA1Notation, isFormula } from '@dxos/compute-hyperformula/types';
import { Annotation, DXN, Obj, Type } from '@dxos/echo';
import { FormInputAnnotation } from '@dxos/echo/Annotation';
import { CollectionItemAnnotation } from '@dxos/schema';

import { addressToIndex, initialize, mapFormulaRefsToIndices } from './SheetUtil';

export const SKILL_KEY = 'org.dxos.skill.sheet';

export type SheetSize = {
  rows: number;
  columns: number;
};

export const CellValue = Schema.Struct({
  // TODO(burdon): How to store dates (datetime, date, time), percentages, etc.
  //  Consider import/export; natural access for other plugins. Special handling for currency (precision).
  // TODO(burdon): Automerge (long string) or short string or number.
  value: Schema.Any,
});

export type CellValue = Schema.Schema.Type<typeof CellValue>;

// TODO(burdon): IMPORTANT: Reconcile with Field definition.
export const Range = Schema.Struct({
  range: Schema.String,
  key: Schema.String,
  value: Schema.String,
});

export type Range = Schema.Schema.Type<typeof Range>;

// TODO(burdon): Visibility, locked, frozen, etc.
export const RowColumnMeta = Schema.Struct({
  size: Schema.optional(Schema.Number),
});

// TODO(burdon): Reconcile col/column (across packages).
// TODO(burdon): Index to all updates when rows/columns are inserted/deleted.
export class Sheet extends Type.makeObject<Sheet>(DXN.make('org.dxos.type.sheet', '0.1.0'))(
  Schema.Struct({
    name: Schema.optional(Schema.String),

    // Sparse map of cells referenced by index.
    cells: Schema.Record({ key: Schema.String, value: CellValue }).pipe(FormInputAnnotation.set(false)),

    // Ordered row indices.
    rows: Schema.Array(Schema.String).pipe(FormInputAnnotation.set(false)),

    // Ordered column indices.
    columns: Schema.Array(Schema.String).pipe(FormInputAnnotation.set(false)),

    // Row metadata referenced by index.
    rowMeta: Schema.Record({ key: Schema.String, value: RowColumnMeta }).pipe(FormInputAnnotation.set(false)),

    // Column metadata referenced by index.
    columnMeta: Schema.Record({ key: Schema.String, value: RowColumnMeta }).pipe(FormInputAnnotation.set(false)),

    // Cell formatting referenced by indexed range.
    ranges: Schema.Array(Range).pipe(FormInputAnnotation.set(false)),
  }).pipe(
    Annotation.IconAnnotation.set({ icon: 'ph--grid-nine--regular', hue: 'indigo' }),
    AppAnnotation.SkillsAnnotation.set([SKILL_KEY]),
    CollectionItemAnnotation.set(true),
  ),
) {}

export type SheetProps = {
  name?: string;
  cells?: Record<string, CellValue>;
} & Partial<SheetSize>;

export const make = ({ name, cells = {}, ...size }: SheetProps = {}) => {
  const sheet = Obj.make(Sheet, { name, cells: {}, rows: [], columns: [], rowMeta: {}, columnMeta: {}, ranges: [] });

  // Initialize and set cells within Obj.update to satisfy change context requirements.
  Obj.update(sheet, (sheet) => {
    initialize(sheet, size);

    if (cells) {
      Object.entries(cells).forEach(([key, { value }]) => {
        const idx = addressToIndex(sheet, addressFromA1Notation(key));
        if (isFormula(value)) {
          value = mapFormulaRefsToIndices(sheet, value);
        }

        sheet.cells[idx] = { value };
      });
    }
  });

  return sheet;
};
